import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import BackButton from '../../../components/common/BackButton';

const G     = '#166534';
const LIGHT = '#DCFCE7';

// listingType can be 'rent', 'lease', or 'sale' — rent/lease are shown "/Month"
const TYPE_META = {
  rent:  { label:'Rent',  color:G },
  lease: { label:'Lease', color:G },
  sale:  { label:'Sale',  color:'#EA580C' },
};
const isRecurring = (t) => t === 'rent' || t === 'lease';

const STATUS_PILL = {
  pending:  { bg:'#FEF9C3', color:'#854D0E', label:'Pending Verification' },
  active:   { bg:'#DCFCE7', color:'#166534', label:'Live' },
  rejected: { bg:'#FEE2E2', color:'#991B1B', label:'Rejected' },
};

export default function LandDetailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/land/${id}`);
      setListing(data.listing || data.data || null);
    } catch {
      setListing(null);
    } finally {
      setLoading(false);
    }
  };

  const sendInquiry = async () => {
    if (!message.trim()) return toast.error('Write a message first');
    setSending(true);
    try {
      await api.post(`/land/${id}/inquire`, { message: message.trim() });
      toast.success('Message sent to the owner');
      setShowContact(false);
      setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif' }}>
        <div style={{ background:G, height:220 }} />
        <div style={{ padding:16 }}>
          <div style={{ height:120, background:'white', borderRadius:16, opacity:.5 }} />
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ fontSize:44, marginBottom:10 }}></div>
        <div style={{ fontSize:14, color:'#6B7280', marginBottom:16 }}>This listing isn't available anymore</div>
        <button onClick={() => navigate(-1)}
          style={{ background:G, color:'white', border:'none', borderRadius:10, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          Go back
        </button>
      </div>
    );
  }

  const photos = listing.photos?.length ? listing.photos : [];
  const owner  = listing.userId || {};

  const DETAIL_ROWS = [
    ['Area',        listing.area ? `${listing.area} ${listing.areaUnit || ''}` : null],
    ['Land Type',   listing.landType],
    ['Frontage',    listing.frontage],
    ['Soil Type',   listing.soilType],
    ['Land Use',    listing.landUse],
  ].filter(([, v]) => v);

  const isOwner = !!(user?._id && owner?._id && String(user._id) === String(owner._id));
  const pill = STATUS_PILL[listing.status] || STATUS_PILL.pending;

  return (
    <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif', paddingBottom:110 }}>

      {/* Photo header — inset rounded card, matching the rest of the app's cards */}
      <div style={{ padding:'calc(12px + env(safe-area-inset-top, 0px)) 14px 0' }}>
        <div style={{ position:'relative', height:250, borderRadius:20, background:LIGHT, overflow:'hidden' }}>
          {photos.length > 0 ? (
            <img src={photos[activePhoto]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          ) : (
            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:60 }}></div>
          )}

          <BackButton dark style={{ position:'absolute', top:14, left:14 }} />

          <span style={{
            position:'absolute', top:14, right:14,
            fontSize:11, fontWeight:700, color:'white', padding:'5px 12px', borderRadius:8,
            background: (TYPE_META[listing.listingType] || TYPE_META.sale).color,
          }}>
            {(TYPE_META[listing.listingType] || TYPE_META.sale).label}
          </span>

          {photos.length > 1 && (
            <div style={{ position:'absolute', bottom:12, left:0, right:0, display:'flex', justifyContent:'center', gap:6 }}>
              {photos.map((_, i) => (
                <button key={i} onClick={() => setActivePhoto(i)}
                  style={{
                    width:6, height:6, borderRadius:'50%', border:'none', padding:0, cursor:'pointer',
                    background: i === activePhoto ? 'white' : 'rgba(255,255,255,.5)',
                  }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {photos.length > 1 && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px 0', overflowX:'auto' }}>
          {photos.map((p, i) => (
            <img key={i} src={p} alt="" onClick={() => setActivePhoto(i)}
              style={{
                width:56, height:56, borderRadius:10, objectFit:'cover', cursor:'pointer', flexShrink:0,
                boxSizing:'border-box',
                border: i === activePhoto ? `2px solid ${G}` : '2px solid transparent',
              }} />
          ))}
        </div>
      )}

      <div style={{ padding:'16px 16px 0' }}>

        <div style={{ fontSize:19, fontWeight:800, color:'#1a1a1a' }}>{listing.title}</div>
        <div style={{ fontSize:12, color:'#9CA3AF', margin:'6px 0 2px' }}> {listing.address}</div>
        <div style={{ fontSize:20, fontWeight:800, color:G, margin:'8px 0' }}>
          ₹{listing.price?.toLocaleString?.() || listing.price}
          {isRecurring(listing.listingType) && <span style={{ fontSize:12, color:'#9CA3AF', fontWeight:400 }}> / Month</span>}
        </div>

        {/* Key details */}
        {DETAIL_ROWS.length > 0 && (
          <div style={{ background:'white', borderRadius:14, border:'0.5px solid #E5E7EB', padding:14, marginTop:14 }}>
            {DETAIL_ROWS.map(([label, value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:12.5 }}>
                <span style={{ color:'#9CA3AF' }}>{label}</span>
                <span style={{ color:'#1a1a1a', fontWeight:600 }}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {listing.description && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1a1a1a', marginBottom:6 }}>Description</div>
            <div style={{ fontSize:13, color:'#4B5563', lineHeight:1.5 }}>{listing.description}</div>
          </div>
        )}

        {/* Amenities */}
        {listing.amenities?.length > 0 && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>Amenities</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {listing.amenities.map(a => (
                <span key={a} style={{ background:LIGHT, color:G, fontSize:11.5, fontWeight:600, padding:'6px 12px', borderRadius:20 }}>{a}</span>
              ))}
            </div>
          </div>
        )}

        {/* Materials */}
        {listing.materials?.length > 0 && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>Materials / Equipment</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {listing.materials.map(m => (
                <span key={m} style={{ background:'#F3F4F6', color:'#374151', fontSize:11.5, fontWeight:600, padding:'6px 12px', borderRadius:20 }}>{m}</span>
              ))}
              {listing.materialsOther && (
                <span style={{ background:'#F3F4F6', color:'#374151', fontSize:11.5, fontWeight:600, padding:'6px 12px', borderRadius:20 }}>{listing.materialsOther}</span>
              )}
            </div>
          </div>
        )}

        {/* Owner */}
        <div style={{ marginTop:18, background:'white', borderRadius:14, border:'0.5px solid #E5E7EB', padding:14, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', background:LIGHT, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
            {owner.profileImageUrl ? <img src={owner.profileImageUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👤'}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a1a1a' }}>{owner.name || 'Property Owner'}</div>
            <div style={{ fontSize:11, color:'#9CA3AF' }}>{owner.locationAddress || 'Listed owner'}</div>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar — always shows Contact Owner; also shows the
          listing's status pill when you're viewing your own listing */}
      <div style={{
        position:'fixed', bottom:0, left:0, right:0, background:'white', borderTop:'1px solid #E5E7EB',
        padding:'12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', display:'flex', alignItems:'center', gap:10, zIndex:150,
      }}>
        {isOwner && (
          <span style={{ fontSize:11, fontWeight:700, color:pill.color, background:pill.bg, padding:'6px 14px', borderRadius:10, whiteSpace:'nowrap' }}>
            {pill.label}
          </span>
        )}
        <button onClick={() => setShowContact(true)}
          style={{ flex:1, background:G, color:'white', border:'none', borderRadius:12, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
          Contact Owner
        </button>
      </div>

      {/* Contact modal */}
      {showContact && (
        <div onClick={() => setShowContact(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:250, display:'flex', alignItems:'flex-end' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'white', width:'100%', borderTopLeftRadius:20, borderTopRightRadius:20, padding:'18px 18px calc(18px + env(safe-area-inset-bottom, 0px))' }}>
            <div style={{ fontSize:15, fontWeight:800, color:'#1a1a1a', marginBottom:10 }}>Message the owner</div>

            {isOwner ? (
              <div style={{ background:'#F3F4F6', color:'#6B7280', fontSize:12.5, borderRadius:12, padding:14, lineHeight:1.5 }}>
                This is your own listing, so you can't send yourself an inquiry. Buyers who view this listing will be able to message you here.
              </div>
            ) : (
              <>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Hi, I'm interested in this property..."
                  rows={4}
                  style={{ width:'100%', border:'1.5px solid #E5E7EB', borderRadius:12, padding:12, fontSize:13, outline:'none', resize:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                />
                <button onClick={sendInquiry} disabled={sending}
                  style={{ width:'100%', marginTop:12, background:G, color:'white', border:'none', borderRadius:12, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer', opacity: sending ? .7 : 1 }}>
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
